import {
  Button,
  LanguageToggle,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  ThemeToggle,
  cn,
} from '@workingclock/ui';
import { Menu } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { signupUrl } from '../../lib/env';
import { LogoMark } from '../brand/logo-mark';

import { Container } from './container';

/**
 * Active nav item gets a Chronometer Gold leading dot (DESIGN.md §5
 * "Navigation": "not a `border-left` stripe"), never a colored side border.
 */
function NavItem({ to, label, onNavigate }: { to: string; label: string; onNavigate?: () => void }) {
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 text-label text-ink transition-colors hover:text-gold',
          isActive && 'font-semibold',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', isActive ? 'bg-gold' : 'bg-transparent')} />
          {label}
        </>
      )}
    </NavLink>
  );
}

export function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation('marketing');
  const { t: tc } = useTranslation('common');

  // Same shape webapp/desktop pass, so the theme dropdown's labels are
  // translated instead of falling back to the component's English defaults.
  const themeLabels = {
    light: tc('theme.light'),
    dark: tc('theme.dark'),
    system: tc('theme.system'),
    trigger: tc('theme.trigger'),
    accentGold: tc('theme.accentGold'),
    accentViolet: tc('theme.accentViolet'),
    accentRose: tc('theme.accentRose'),
  };

  const navItems = [
    { to: '/features', label: t('header.nav.features') },
    { to: '/pricing', label: t('header.nav.pricing') },
    { to: '/developers', label: t('header.nav.developers') },
    { to: '/download', label: t('header.nav.download') },
    { to: '/contact', label: t('header.nav.support') },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-bg">
      <Container className="flex h-16 items-center justify-between">
        <NavLink to="/" className="flex items-center gap-2" aria-label={t('header.homeAriaLabel')}>
          <LogoMark />
          <span className="text-title text-ink">{t('brand.name')}</span>
        </NavLink>

        <nav aria-label={t('header.primaryNavAriaLabel')} className="hidden items-center gap-6 md:flex">
          {navItems.map((item) => (
            <NavItem key={item.to} to={item.to} label={item.label} />
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageToggle triggerLabel={tc('language.trigger')} />
          <ThemeToggle labels={themeLabels} />
          <Button asChild>
            <a href={signupUrl}>{t('header.ctaStartFree')}</a>
          </Button>
        </div>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label={t('header.openMenuAriaLabel')}
          >
            <Menu aria-hidden />
          </Button>
          <SheetContent side="right" className="w-4/5 max-w-xs">
            <SheetHeader>
              <SheetTitle>{t('header.menuTitle')}</SheetTitle>
            </SheetHeader>
            <nav aria-label={t('header.mobileNavAriaLabel')} className="flex flex-col gap-5">
              {navItems.map((item) => (
                <NavItem key={item.to} to={item.to} label={item.label} onNavigate={() => setMobileOpen(false)} />
              ))}
            </nav>
            <div className="mt-4 flex items-center gap-3">
              <LanguageToggle triggerLabel={tc('language.trigger')} />
              <ThemeToggle labels={themeLabels} />
              <Button asChild className="flex-1">
                <a href={signupUrl}>{t('header.ctaStartFree')}</a>
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </Container>
    </header>
  );
}
